import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Check, Building2, User, Euro, Calendar,
  FileText, Send, Mail, AlertTriangle, CheckCircle2, Sparkles,
  Download, Printer, Clock, Shield, ChevronRight, Home, Phone,
  CreditCard, Loader2
} from 'lucide-react';
import PaymentMethodSelector from '@/components/PaymentMethodSelector';

const GenerateurMiseEnDemeure = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Expéditeur (Syndic)
    syndic_type: '', // 'benevole' ou 'professionnel'
    syndic_nom: '',
    syndic_prenom: '',
    syndic_adresse: '',
    syndic_code_postal: '',
    syndic_ville: '',
    syndic_email: '',
    syndic_telephone: '',
    copropriete_nom: '',
    copropriete_adresse: '',
    
    // Destinataire (Débiteur)
    debiteur_civilite: '',
    debiteur_nom: '',
    debiteur_prenom: '',
    debiteur_adresse: '',
    debiteur_code_postal: '',
    debiteur_ville: '',
    debiteur_email: '',
    
    // Détails de la dette
    montant_principal: '',
    date_echeance: '',
    nature_charges: 'charges_courantes', // charges_courantes, travaux, mixte
    detail_charges: '',
    
    // Options
    delai_paiement: '15', // jours
    mention_interets: true,
    mention_judiciaire: true,
  });
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const steps = [
    { id: 'intro', title: 'Bienvenue', icon: Sparkles },
    { id: 'syndic', title: 'Votre copropriété', icon: Building2 },
    { id: 'debiteur', title: 'Le débiteur', icon: User },
    { id: 'dette', title: 'La dette', icon: Euro },
    { id: 'options', title: 'Options', icon: FileText },
    { id: 'preview', title: 'Aperçu', icon: Check },
    { id: 'envoi', title: 'Envoi', icon: Send },
  ];

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const generateLetter = () => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    
    const letter = `
${formData.copropriete_nom}
Syndicat des copropriétaires
${formData.copropriete_adresse}

${formData.syndic_type === 'benevole' ? `${formData.syndic_prenom} ${formData.syndic_nom}` : formData.syndic_nom}
Syndic ${formData.syndic_type === 'benevole' ? 'bénévole' : 'professionnel'}
${formData.syndic_adresse}
${formData.syndic_code_postal} ${formData.syndic_ville}
${formData.syndic_email ? `Email : ${formData.syndic_email}` : ''}
${formData.syndic_telephone ? `Tél : ${formData.syndic_telephone}` : ''}

${formData.syndic_ville}, le ${formattedDate}

${formData.debiteur_civilite} ${formData.debiteur_prenom} ${formData.debiteur_nom}
${formData.debiteur_adresse}
${formData.debiteur_code_postal} ${formData.debiteur_ville}

Lettre recommandée avec accusé de réception

Objet : MISE EN DEMEURE DE PAYER – Charges de copropriété impayées

${formData.debiteur_civilite} ${formData.debiteur_nom},

En ma qualité de syndic ${formData.syndic_type === 'benevole' ? 'bénévole' : ''} de la copropriété « ${formData.copropriete_nom} » sise ${formData.copropriete_adresse}, je me vois dans l'obligation de vous adresser la présente mise en demeure.

En effet, malgré les appels de fonds qui vous ont été adressés, je constate que vous restez redevable envers le syndicat des copropriétaires de la somme de :

    ${parseFloat(formData.montant_principal).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
    
au titre des ${formData.nature_charges === 'charges_courantes' ? 'charges courantes de copropriété' : formData.nature_charges === 'travaux' ? 'travaux votés en assemblée générale' : 'charges courantes et travaux votés en assemblée générale'}${formData.detail_charges ? ` (${formData.detail_charges})` : ''}, dont l'échéance était fixée au ${new Date(formData.date_echeance).toLocaleDateString('fr-FR')}.

Par la présente, je vous mets en demeure de procéder au règlement intégral de cette somme dans un délai de ${formData.delai_paiement} jours à compter de la réception de ce courrier.

${formData.mention_interets ? `Conformément à l'article 1231-6 du Code civil, des intérêts de retard au taux légal sont dus de plein droit à compter de la présente mise en demeure, sans qu'il soit nécessaire de vous adresser un nouvel avertissement.

` : ''}${formData.mention_judiciaire ? `À défaut de paiement dans le délai imparti, je me verrai contraint d'engager une procédure judiciaire à votre encontre (injonction de payer ou assignation devant le tribunal), dont les frais seront mis à votre charge, conformément à l'article 10-1 de la loi du 10 juillet 1965.

Je vous rappelle que le syndicat des copropriétaires bénéficie d'un privilège spécial immobilier (article 2374 du Code civil) garantissant le recouvrement des charges sur votre lot en cas de vente.

` : ''}Je reste néanmoins à votre disposition pour examiner toute proposition de règlement amiable, notamment la mise en place d'un échéancier, sous réserve de votre engagement écrit.

Je vous prie de croire, ${formData.debiteur_civilite} ${formData.debiteur_nom}, à l'expression de mes salutations distinguées.

${formData.syndic_type === 'benevole' ? `${formData.syndic_prenom} ${formData.syndic_nom}` : formData.syndic_nom}
Syndic de la copropriété
    `.trim();
    
    setGeneratedLetter(letter);
    setShowPreview(true);
  };

  const handleClearbusCheckout = async () => {
    if (!formData.debiteur_email) {
      toast.error("L'email du destinataire est requis pour l'envoi Clearbus");
      return;
    }

    setIsProcessingPayment(true);
    
    try {
      // Appeler l'endpoint de checkout pour le générateur
      const response = await apiClient.post('/services/mise-en-demeure/generateur/checkout', {
        debiteur_email: formData.debiteur_email,
        debiteur_nom: `${formData.debiteur_civilite} ${formData.debiteur_prenom} ${formData.debiteur_nom}`.trim(),
        montant: parseFloat(formData.montant_principal) || 0,
        copropriete_nom: formData.copropriete_nom,
        lettre_content: generatedLetter
      });
      
      if (response.data.checkout_url) {
        // Rediriger vers Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Erreur lors de la création du paiement");
      }
    } catch (error) {
      console.error('Erreur checkout:', error);
      const message = getErrorMessage(error, "Erreur lors de la création du paiement");
      if (message) toast.error(message);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Gestion du succès PayPal
  const handlePaymentSuccess = (commandeId) => {
    navigate(`/paiement-succes?commande=${commandeId}`);
  };

  // Données de paiement pour le sélecteur
  const getPaymentData = () => ({
    debiteur_nom: `${formData.debiteur_civilite} ${formData.debiteur_prenom} ${formData.debiteur_nom}`.trim(),
    debiteur_email: formData.debiteur_email,
    montant: parseFloat(formData.montant_principal) || 0,
    copropriete_nom: formData.copropriete_nom,
    lettre_content: generatedLetter
  });

  // Composant pour les boutons de choix stylisés
  const ChoiceButton = ({ selected, onClick, icon: Icon, title, description }) => (
    <button
      onClick={onClick}
      className={`w-full p-5 rounded-2xl border-2 text-left transition-all ${
        selected 
          ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100' 
          : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          selected ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500'
        }`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold ${selected ? 'text-emerald-700' : 'text-slate-900'}`}>{title}</h3>
            {selected && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
          </div>
          {description && <p className="text-slate-500 text-sm mt-1">{description}</p>}
        </div>
      </div>
    </button>
  );

  // Composant Input stylisé
  const StyledInput = ({ label, type = 'text', value, onChange, placeholder, required, icon: Icon }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label} {required && <span className="text-red-500">*</span>}</label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full ${Icon ? 'pl-12' : 'pl-4'} pr-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none`}
        />
      </div>
    </div>
  );

  // Rendu des étapes
  const renderStep = () => {
    switch (steps[currentStep].id) {
      case 'intro':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-emerald-200">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Créez votre mise en demeure
            </h2>
            <p className="text-slate-600 text-lg mb-8 max-w-md mx-auto">
              Rédigez une mise en demeure conforme en quelques clics, 
              puis envoyez-la en recommandé électronique.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 max-w-lg mx-auto mb-8">
              {[
                { icon: Clock, text: '2 minutes' },
                { icon: Shield, text: 'Valeur juridique' },
                { icon: Euro, text: '8,90€ en Clearbus' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-center gap-2 text-slate-600">
                  <item.icon className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
            <button
              onClick={nextStep}
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-all text-lg shadow-lg shadow-emerald-200"
            >
              Commencer
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        );

      case 'syndic':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Votre copropriété</h2>
              <p className="text-slate-500">Informations sur le syndic et la copropriété</p>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Vous êtes :</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <ChoiceButton
                  selected={formData.syndic_type === 'benevole'}
                  onClick={() => updateForm('syndic_type', 'benevole')}
                  icon={User}
                  title="Syndic bénévole"
                  description="Copropriétaire élu par l'AG"
                />
                <ChoiceButton
                  selected={formData.syndic_type === 'professionnel'}
                  onClick={() => updateForm('syndic_type', 'professionnel')}
                  icon={Building2}
                  title="Syndic professionnel"
                  description="Cabinet de syndic"
                />
              </div>
            </div>

            {formData.syndic_type && (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="grid sm:grid-cols-2 gap-4">
                  {formData.syndic_type === 'benevole' ? (
                    <>
                      <StyledInput
                        label="Votre prénom"
                        value={formData.syndic_prenom}
                        onChange={(v) => updateForm('syndic_prenom', v)}
                        placeholder="Jean"
                        required
                      />
                      <StyledInput
                        label="Votre nom"
                        value={formData.syndic_nom}
                        onChange={(v) => updateForm('syndic_nom', v)}
                        placeholder="Dupont"
                        required
                      />
                    </>
                  ) : (
                    <div className="sm:col-span-2">
                      <StyledInput
                        label="Nom du cabinet"
                        value={formData.syndic_nom}
                        onChange={(v) => updateForm('syndic_nom', v)}
                        placeholder="Cabinet XYZ Gestion"
                        required
                      />
                    </div>
                  )}
                </div>
                
                <StyledInput
                  label="Nom de la copropriété"
                  value={formData.copropriete_nom}
                  onChange={(v) => updateForm('copropriete_nom', v)}
                  placeholder="Résidence Les Jardins"
                  required
                  icon={Building2}
                />
                
                <StyledInput
                  label="Adresse de la copropriété"
                  value={formData.copropriete_adresse}
                  onChange={(v) => updateForm('copropriete_adresse', v)}
                  placeholder="12 rue des Lilas, 75001 Paris"
                  required
                  icon={Home}
                />

                <div className="grid sm:grid-cols-3 gap-4">
                  <StyledInput
                    label="Votre adresse"
                    value={formData.syndic_adresse}
                    onChange={(v) => updateForm('syndic_adresse', v)}
                    placeholder="12 rue des Lilas"
                  />
                  <StyledInput
                    label="Code postal"
                    value={formData.syndic_code_postal}
                    onChange={(v) => updateForm('syndic_code_postal', v)}
                    placeholder="75001"
                  />
                  <StyledInput
                    label="Ville"
                    value={formData.syndic_ville}
                    onChange={(v) => updateForm('syndic_ville', v)}
                    placeholder="Paris"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <StyledInput
                    label="Email"
                    type="email"
                    value={formData.syndic_email}
                    onChange={(v) => updateForm('syndic_email', v)}
                    placeholder="syndic@copro.fr"
                    icon={Mail}
                  />
                  <StyledInput
                    label="Téléphone"
                    value={formData.syndic_telephone}
                    onChange={(v) => updateForm('syndic_telephone', v)}
                    placeholder="06 12 34 56 78"
                    icon={Phone}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'debiteur':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Le copropriétaire débiteur</h2>
              <p className="text-slate-500">À qui envoyer la mise en demeure ?</p>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-700 mb-3">Civilité :</p>
              <div className="flex gap-4">
                {['Madame', 'Monsieur', 'Madame, Monsieur'].map((civ) => (
                  <button
                    key={civ}
                    onClick={() => updateForm('debiteur_civilite', civ)}
                    className={`px-5 py-3 rounded-xl border-2 font-medium transition-all ${
                      formData.debiteur_civilite === civ
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 text-slate-600 hover:border-emerald-300'
                    }`}
                  >
                    {civ}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <StyledInput
                label="Prénom"
                value={formData.debiteur_prenom}
                onChange={(v) => updateForm('debiteur_prenom', v)}
                placeholder="Marie"
                required
              />
              <StyledInput
                label="Nom"
                value={formData.debiteur_nom}
                onChange={(v) => updateForm('debiteur_nom', v)}
                placeholder="Martin"
                required
              />
            </div>

            <StyledInput
              label="Adresse postale"
              value={formData.debiteur_adresse}
              onChange={(v) => updateForm('debiteur_adresse', v)}
              placeholder="Appartement 12, Résidence Les Jardins"
              required
              icon={Home}
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <StyledInput
                label="Code postal"
                value={formData.debiteur_code_postal}
                onChange={(v) => updateForm('debiteur_code_postal', v)}
                placeholder="75001"
                required
              />
              <StyledInput
                label="Ville"
                value={formData.debiteur_ville}
                onChange={(v) => updateForm('debiteur_ville', v)}
                placeholder="Paris"
                required
              />
            </div>

            <StyledInput
              label="Email (pour Clearbus)"
              type="email"
              value={formData.debiteur_email}
              onChange={(v) => updateForm('debiteur_email', v)}
              placeholder="marie.martin@email.com"
              icon={Mail}
            />
            <p className="text-xs text-slate-500 -mt-2">
              L'email est requis pour l'envoi en recommandé électronique (Clearbus)
            </p>
          </div>
        );

      case 'dette':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Détails de la dette</h2>
              <p className="text-slate-500">Quel montant est réclamé ?</p>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-100">
              <label className="block text-sm font-medium text-slate-700 mb-2">Montant total dû</label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.montant_principal}
                  onChange={(e) => updateForm('montant_principal', e.target.value)}
                  placeholder="0.00"
                  className="w-full text-4xl font-bold text-red-600 bg-transparent border-none outline-none placeholder:text-red-300"
                />
                <span className="absolute right-0 top-1/2 -translate-y-1/2 text-2xl text-red-400">€</span>
              </div>
            </div>

            <StyledInput
              label="Date d'échéance initiale"
              type="date"
              value={formData.date_echeance}
              onChange={(v) => updateForm('date_echeance', v)}
              required
              icon={Calendar}
            />

            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-700">Nature des charges impayées :</p>
              <div className="space-y-3">
                {[
                  { value: 'charges_courantes', title: 'Charges courantes', desc: 'Entretien, gardien, ascenseur...' },
                  { value: 'travaux', title: 'Travaux votés en AG', desc: 'Ravalement, toiture, parties communes...' },
                  { value: 'mixte', title: 'Charges courantes + travaux', desc: 'Cumul des deux types' },
                ].map((option) => (
                  <ChoiceButton
                    key={option.value}
                    selected={formData.nature_charges === option.value}
                    onClick={() => updateForm('nature_charges', option.value)}
                    icon={FileText}
                    title={option.title}
                    description={option.desc}
                  />
                ))}
              </div>
            </div>

            <StyledInput
              label="Précisions (optionnel)"
              value={formData.detail_charges}
              onChange={(v) => updateForm('detail_charges', v)}
              placeholder="Ex: appels de fonds T3 et T4 2025"
            />
          </div>
        );

      case 'options':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Options de la mise en demeure</h2>
              <p className="text-slate-500">Personnalisez votre courrier</p>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-700">Délai accordé pour le paiement :</p>
              <div className="flex gap-3">
                {['8', '15', '30'].map((days) => (
                  <button
                    key={days}
                    onClick={() => updateForm('delai_paiement', days)}
                    className={`flex-1 py-4 rounded-xl border-2 font-semibold transition-all ${
                      formData.delai_paiement === days
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 text-slate-600 hover:border-emerald-300'
                    }`}
                  >
                    {days} jours
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-slate-700">Mentions à inclure :</p>
              
              <label className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-emerald-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={formData.mention_interets}
                  onChange={(e) => updateForm('mention_interets', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <p className="font-medium text-slate-900">Mention des intérêts de retard</p>
                  <p className="text-sm text-slate-500">Rappel que les intérêts légaux courent à compter de la mise en demeure</p>
                </div>
              </label>

              <label className="flex items-start gap-4 p-4 rounded-xl border border-slate-200 hover:border-emerald-300 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={formData.mention_judiciaire}
                  onChange={(e) => updateForm('mention_judiciaire', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <p className="font-medium text-slate-900">Avertissement de poursuites judiciaires</p>
                  <p className="text-sm text-slate-500">Mention de l'injonction de payer et du privilège du syndicat</p>
                </div>
              </label>
            </div>
          </div>
        );

      case 'preview':
        if (!generatedLetter) {
          generateLetter();
        }
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Votre mise en demeure</h2>
              <p className="text-slate-500">Vérifiez le contenu avant envoi</p>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-2xl p-6 md:p-8 shadow-inner max-h-[500px] overflow-y-auto">
              <pre className="whitespace-pre-wrap font-serif text-sm text-slate-800 leading-relaxed">
                {generatedLetter}
              </pre>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  const blob = new Blob([generatedLetter], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `mise_en_demeure_${formData.debiteur_nom}_${new Date().toISOString().split('T')[0]}.txt`;
                  a.click();
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                <Download className="w-5 h-5" />
                Télécharger
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
              >
                <Printer className="w-5 h-5" />
                Imprimer
              </button>
            </div>
          </div>
        );

      case 'envoi':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Envoyer la mise en demeure</h2>
              <p className="text-slate-500">L'envoi en recommandé est obligatoire pour avoir une valeur juridique</p>
            </div>

            {/* IMPORTANT - Obligation légale */}
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-red-800 text-lg mb-2">⚖️ Obligation légale</h3>
                  <p className="text-red-700 text-sm leading-relaxed">
                    L'article 19 de la loi du 10 juillet 1965 impose l'envoi d'une <strong>mise en demeure par lettre recommandée avec accusé de réception</strong> avant toute action en justice.
                  </p>
                  <p className="text-red-600 text-sm mt-2 font-medium">
                    Sans ce recommandé, votre procédure sera <strong>irrecevable</strong> devant le tribunal.
                  </p>
                </div>
              </div>
            </div>

            {/* Stat persuasive */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 text-white">
              <div className="flex items-center gap-4">
                <div className="text-5xl font-black text-emerald-400">78%</div>
                <div>
                  <p className="font-semibold text-lg">des débiteurs règlent après réception du recommandé</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Le recommandé formalise la situation et marque le début d'une procédure. 
                    La plupart des copropriétaires préfèrent régler plutôt que d'aller au tribunal.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Option 1 : Envoi recommandé électronique */}
              <div className="border-2 border-emerald-500 rounded-2xl p-6 bg-gradient-to-br from-emerald-50 to-green-50 relative overflow-hidden">
                <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  RECOMMANDÉ
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Send className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 text-xl mb-2">Envoyer en recommandé électronique</h3>
                    <p className="text-slate-600 mb-4">
                      Envoi <strong>instantané</strong> avec accusé de réception. Valeur juridique identique au recommandé postal 
                      <span className="text-emerald-600 font-medium"> (Décret n°2018-347)</span>.
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span>Valeur juridique garantie</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span>Accusé de réception</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span>Envoi en 2 minutes</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                        <span>Preuve de dépôt immédiate</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      disabled={!formData.debiteur_email || isProcessingPayment}
                      className="w-full bg-emerald-500 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                      data-testid="open-payment-modal-btn"
                    >
                      {isProcessingPayment ? (
                        <>
                          <Loader2 className="w-6 h-6 animate-spin" />
                          Traitement en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-6 h-6" />
                          Envoyer maintenant — 8,90 €
                        </>
                      )}
                    </button>
                    <p className="text-xs text-slate-500 mt-3 text-center flex items-center justify-center gap-2">
                      <Shield className="w-4 h-4" />
                      Paiement sécurisé · Carte bancaire ou PayPal
                    </p>
                    {!formData.debiteur_email && (
                      <p className="text-amber-600 text-sm mt-3 text-center font-medium">
                        ⚠️ Renseignez l'email du destinataire à l'étape 2 pour activer l'envoi
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Option 2 : Télécharger PDF */}
              <div className="border border-slate-200 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Download className="w-7 h-7 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-700 text-lg mb-2">Télécharger le PDF</h3>
                    <p className="text-slate-500 text-sm mb-4">
                      Téléchargez la lettre et envoyez-la vous-même en recommandé AR via La Poste.
                    </p>
                    <button
                      onClick={() => window.print()}
                      className="w-full bg-slate-100 text-slate-700 py-3 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <Download className="w-5 h-5" />
                      Télécharger en PDF
                    </button>
                    <p className="text-xs text-amber-600 mt-2 text-center font-medium">
                      ⚠️ N'oubliez pas : l'envoi doit être fait en recommandé avec AR
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 text-white rounded-xl p-5 text-sm">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-amber-400">Le saviez-vous ?</strong>
                  <p className="text-slate-300 mt-1">
                    La lettre recommandée électronique a <strong className="text-white">exactement la même valeur juridique</strong> que le recommandé postal classique depuis 2018. 
                    Elle permet de déclencher la clause d'exigibilité anticipée (article 19-2 de la loi de 1965).
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (steps[currentStep].id) {
      case 'intro':
        return true;
      case 'syndic':
        return formData.syndic_type && formData.copropriete_nom && formData.copropriete_adresse;
      case 'debiteur':
        return formData.debiteur_civilite && formData.debiteur_nom && formData.debiteur_adresse && formData.debiteur_ville;
      case 'dette':
        return formData.montant_principal && formData.date_echeance;
      case 'options':
        return true;
      case 'preview':
        return true;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <Helmet>
        <title>Générateur de Mise en Demeure Gratuit | Copropriété | copropriete.app</title>
        <meta name="description" content="Créez gratuitement une mise en demeure pour charges de copropriété impayées. Envoi en recommandé électronique Clearbus pour 8,90€. Valeur juridique garantie." />
        <meta name="keywords" content="mise en demeure copropriété, modèle mise en demeure charges impayées, lettre recommandée copropriété, générateur mise en demeure gratuit" />
        <link rel="canonical" href="https://copropriete.app/generateur-mise-en-demeure" />
      </Helmet>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/impayes-copropriete" className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Retour au guide</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_5700d295-d3ed-4629-8765-e98307778f89/artifacts/bbyq8lme_icone_verre_vert_512.png" 
              alt="copropriete.app" 
              className="h-8 w-8"
            />
            <span className="text-lg font-bold text-slate-900">copropriete.app</span>
          </Link>
          <div className="w-24" /> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        {currentStep > 0 && currentStep < steps.length - 1 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {steps.slice(1, -1).map((step, index) => (
                <div 
                  key={step.id}
                  className={`flex items-center ${index < steps.length - 3 ? 'flex-1' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    currentStep > index + 1 
                      ? 'bg-emerald-500 text-white' 
                      : currentStep === index + 1 
                        ? 'bg-emerald-500 text-white ring-4 ring-emerald-100' 
                        : 'bg-slate-200 text-slate-500'
                  }`}>
                    {currentStep > index + 1 ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  {index < steps.length - 3 && (
                    <div className={`flex-1 h-1 mx-2 rounded ${
                      currentStep > index + 1 ? 'bg-emerald-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-slate-500">
              Étape {currentStep} sur {steps.length - 2} — {steps[currentStep].title}
            </p>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 p-6 md:p-10">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        {currentStep > 0 && currentStep < steps.length - 1 && (
          <div className="flex gap-4 mt-6">
            <button
              onClick={prevStep}
              className="flex-1 py-4 rounded-xl border-2 border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Précédent
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="flex-1 py-4 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {currentStep === steps.length - 2 ? 'Voir l\'aperçu' : 'Continuer'}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </main>

      {/* Modal de sélection du mode de paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => !isProcessingPayment && setShowPaymentModal(false)}
          />
          
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            {/* Bouton fermer */}
            <button
              onClick={() => !isProcessingPayment && setShowPaymentModal(false)}
              disabled={isProcessingPayment}
              className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <PaymentMethodSelector
              onStripeCheckout={handleClearbusCheckout}
              onPaymentSuccess={handlePaymentSuccess}
              paymentData={getPaymentData()}
              isProcessing={isProcessingPayment}
              setIsProcessing={setIsProcessingPayment}
              amount={8.90}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateurMiseEnDemeure;
